import find from 'lodash/find';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import range from 'lodash/range';
import union from 'lodash/union';
import values from 'lodash/values';
import { createSelector } from 'reselect';
import { selectUserCountryCode } from '@gandi/authentication';
import { selectCurrentId } from '@gandi/redux-sharing';
import { localeSelector } from '@gandi/translate';
import { applyDecoratorsOnProduct } from './libs/decorators';
import { sortItems, productsOrderDef, productOptionsOrderDef } from './libs/sortItems';
const productsMergeKeys = {
  mailboxv2: ['payload.email', 'payload.fqdn', 'payload.mailbox_type', 'process', 'payload.duration', 'payload.fake_renew']
};
const domainProductType = {
  productType: 'domain'
};
function passProps(state, props) {
  return props;
}
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];
export const selectCartUUID = state => get(state, 'cart.uuid', '');
export const selectCartReselleeID = state => get(state, 'cart.reselleeId', '');
export const selectCartProductOwner = state => get(state, 'cart.productOwner', '');
export const selectCartOwnerIsReseller = state => state.cart.isOwnerReseller || false;
export const selectCartReferer = state => state.cart.referer || '';
export const selectCartIsBeingCreated = state => !!state.cart.isBeingCreated;
export const selectCartIsWorking = state => !!state.cart.isWorking;
export const selectCartIsLoaded = state => !!state.cart.isLoaded;
export const selectCartIsFailure = state => state.cart.isFailure;
export const selectCartCurrency = state => state.cart.currency;
export const selectCartGrid = state => state.cart.grid;
export const selectCartAffiliate = state => state.cart.affiliate;
const selectCountryCode = state => selectUserCountryCode(state);
export const selectRawProducts = state => get(state, 'cart.products', EMPTY_OBJECT);
export const selectRawProductsArray = createSelector([selectRawProducts], rawProducts => values(rawProducts));
export const selectRawContracts = state => get(state, 'cart.contracts');
export const selectRawDiscounts = state => get(state, 'cart.discounts');
export const selectRawDiscountsArray = createSelector([selectRawDiscounts], rawDiscounts => values(rawDiscounts));
export const selectAllDiscounts = state => state.cart.allDiscounts || EMPTY_ARRAY;
const EMPTY_UNUSED_DISCOUNTS_ARRAY = [];
export const selectUnusedDiscounts = state => state.cart.unusedDiscounts || EMPTY_UNUSED_DISCOUNTS_ARRAY;
export const selectDiscounts = createSelector(selectRawDiscounts, discounts => values(discounts || {}).sort((a, b) => {
  if (!a.code || !b.code) return 0;
  return a.code > b.code ? 1 : -1;
}));
export const selectOneDiscount = createSelector(selectRawDiscounts, passProps, (discounts, {
  code
}) => get(discounts, code));
export const selectDoesAnyCartProductHaveDiscount = createSelector(selectRawProductsArray, rawProducts => rawProducts.filter(product => Array.isArray(product.prices) && product.prices.filter(price => price.item_type === 'discount').length > 0).length > 0);
export const selectCanChooseResellee = createSelector(selectRawProductsArray, rawProducts => rawProducts.filter(product => product.productType === 'domain' && ['create', 'transfer'].indexOf(product.process) > -1 || product.productType === 'certificate' && product.process === 'create').length > 0);
export const selectReselleeIdOfCartProducts = createSelector(selectRawProductsArray, rawProducts => {
  const reselleeId = rawProducts.filter(product => product.payload.resellee_id !== undefined)[0];
  return reselleeId && reselleeId.payload && reselleeId.payload.resellee_id ? reselleeId.payload.resellee_id : '';
});
export const selectProducts = createSelector([selectRawProductsArray, selectRawDiscountsArray], (products, discounts) => {
  const mergedProducts = products.reduce((allProducts, product) => {
    const {
      id
    } = product;
    const getDiscountDetails = discountCode => discounts.find(d => d.code === discountCode);
    if (Object.keys(productsMergeKeys).indexOf(product.productType) !== -1) {
      const previousProductId = findKey(allProducts, previousProduct => values(get(productsMergeKeys, product.productType, [])).every(key => get(product, key) === get(previousProduct, key)));
      if (previousProductId) {
        allProducts[previousProductId].quantity += 1;
        allProducts[previousProductId].computedTotalDiscounts.push(...(product.discounts || []).map(getDiscountDetails));
        allProducts[previousProductId].ids.push(product.id);
        allProducts[previousProductId].computedTotalPrices.beforeTaxes += product.total?.before_taxes ?? 0;
        allProducts[previousProductId].computedTotalPrices.beforeDiscount += (product.total?.before_discount ?? product.total?.before_taxes) || 0;
        if (product.total?.before_discount_before_taxes) {
          allProducts[previousProductId].computedTotalPrices.beforeDiscountAfterTaxes += product.total?.before_discount_after_taxes || 0;
          allProducts[previousProductId].computedTotalPrices.beforeDiscountBeforeTaxes += product.total?.before_discount_before_taxes || 0;
        }
        allProducts[previousProductId].computedTotalPrices.afterTaxes += product.total?.after_taxes ?? 0;
        return allProducts;
      }
    }
    const computedTotalPrices = {
      beforeDiscount: (product.total?.before_discount ?? product.total?.before_taxes) || 0,
      beforeTaxes: product.total?.before_taxes ?? 0,
      afterTaxes: product.total?.after_taxes ?? 0,
      rate: (product.total?.taxes || []).reduce((acc, taxes) => acc + (taxes.rate || 0), 0),
      ...(product.total?.before_discount_before_taxes ? {
        beforeDiscountAfterTaxes: product.total?.before_discount_after_taxes,
        beforeDiscountBeforeTaxes: product.total?.before_discount_before_taxes
      } : undefined)
    };
    return {
      ...allProducts,
      [id]: {
        ...product,
        ids: [product.id],
        computedTotalDiscounts: [...(product.discounts || []).map(getDiscountDetails)],
        computedTotalPrices,
        quantity: 1
      }
    };
  }, {});
  return sortItems(values(mergedProducts).map(applyDecoratorsOnProduct), productsOrderDef);
});
const selectOneProductByIdFactory = () => createSelector(selectRawProducts, passProps, (products, props) => {
  const product = products && products[props.productId];
  return product ? applyDecoratorsOnProduct(product) : undefined;
});
export const selectOneProductById = selectOneProductByIdFactory();
const identityForFQDN = fqdn => obj => fqdn && (obj.payload && obj.payload.fqdn === fqdn || obj.identifier === fqdn);
export const selectOneProductByFQDN = createSelector(selectRawProducts, passProps, (products, props) => {
  const productId = findKey(products, identityForFQDN(props.fqdn)) || '';
  const product = products && products[productId];
  return product ? applyDecoratorsOnProduct(product) : undefined;
});
const identityForIdentifier = identifier => obj => identifier && obj.identifier === identifier;
const selectOneProductByIdentifier = createSelector(selectRawProducts, passProps, (products, props) => {
  const productId = findKey(products, identityForIdentifier(props.identifier)) || '';
  const product = products && products[productId];
  return product ? applyDecoratorsOnProduct(product) : undefined;
});
export const selectOneProduct = (state, props) => {
  let product;
  if ('productId' in props) {
    product = selectOneProductById(state, {
      productId: String(props.productId)
    });
  }
  if ('fqdn' in props) {
    product = selectOneProductByFQDN(state, {
      fqdn: String(props.fqdn)
    });
  }
  if ('identifier' in props) {
    product = selectOneProductByIdentifier(state, {
      identifier: String(props.identifier)
    });
  }
  return product ? applyDecoratorsOnProduct(product) : undefined;
};
const passProductId = (_, productId) => productId;
const EMPTY_OPTIONS_ARRAY = [];
export const selectProductOptionsFactory = () => createSelector([selectRawProducts, passProductId], (products, productId) => {
  const product = products[productId];
  if (!product || !Array.isArray(product.options) || !product.options.length) {
    return EMPTY_OPTIONS_ARRAY;
  }
  const options = product.options.map(option => applyDecoratorsOnProduct({
    ...product,
    ...option
  }));
  return sortItems(options, productOptionsOrderDef);
});
export const selectProductOptionsUIFactory = () => {
  const selectProductOptions = selectProductOptionsFactory();
  return createSelector([selectProductOptions], options => options.filter(option => !(option.productType === 'corporate' && option.description === 'misc_fees')));
};
export const selectCountTotalProducts = createSelector(selectProducts, products => products.reduce((acc, p) => acc + p.quantity, 0));
const EMPTY_PRODUCT_ERROR_ARRAY = [];
export const selectOneProductErrors = createSelector([selectOneProduct], product => product?.errors || EMPTY_PRODUCT_ERROR_ARRAY);
export const selectTotalProducts = createSelector(selectRawProducts, products => Object.keys(products || {}).length);
export const selectProductsByType = createSelector(selectProducts, passProps, (products, props) => products.filter(product => product.productType === props.productType));
export const selectProductsByTypeAndProcess = createSelector(selectProductsByType, passProps, (products, props) => products.filter(product => product.process === props.process));
export const selectCanChangeCartOwner = createSelector([selectProducts], products => !products.some(product => product.productType === 'mailboxv2' && product.process !== 'renew' || product.productType === 'paas' && product.process !== 'create'));
export const selectShouldSuggestUpsell = createSelector(selectProducts, products => products.length > 0 && products.length <= 2 && products.every(product => product.productType === 'domain' && product.process === 'renew'));
export const selectShouldSuggestCrossSell = createSelector([selectProducts], products => products.length > 0 && products.length < 5 && products.every(product => product.productType === 'domain' && product.process === 'create'));
const selectDomainRenewProducts = createSelector([state => selectProductsByType(state, domainProductType)], domainProducts => domainProducts.filter(product => product.process === 'renew'));
export const selectDomainRenewProductsFQDNs = createSelector(selectDomainRenewProducts, products => products.filter(product => 'identifier' in product).map(product => String(product.identifier)));
const selectDomainRenewHasPremium = createSelector(selectDomainRenewProducts, products => products.some(product => product.isPremium));
export const selectDomainCreateProducts = createSelector([state => selectProductsByType(state, domainProductType)], domainProducts => domainProducts.filter(product => product.process === 'create'));
export const selectDomainCreateOrTransferProducts = createSelector([state => selectProductsByType(state, domainProductType)], domainProducts => domainProducts.filter(product => product.process === 'create' || product.process === 'transfer'));
export const selectHasDomainCorporateProducts = createSelector([state => selectProductsByType(state, domainProductType)], domainProducts => domainProducts.filter(product => product.payload.corporate).length > 0);
const corporateProductType = {
  productType: 'corporate'
};
export const selectHasCorporateCreateProduct = createSelector([state => selectProductsByType(state, corporateProductType)], corpoProducts => corpoProducts.filter(product => product.process === 'create').length > 0);
export const selectProductsFromFqdn = createSelector([selectRawProductsArray, passProps], (products, fqdn) => products.filter(product => product.identifier === fqdn || product.label === fqdn || product.payload.fqdn === fqdn));
export const selectDomainFromFqdn = createSelector(selectProductsFromFqdn, productsFromFqdn => productsFromFqdn.find(product => product.productType === 'domain'));
export const selectDomainProductsToPatchWithOwner = createSelector(passProps, selectDomainCreateOrTransferProducts, (props, domainCreateOrTransferProducts) => {
  if (!props.forcePatch) {
    return domainCreateOrTransferProducts.filter(product => props.sharingId !== get(product, 'payload.lastCurrentOrgaId', ''));
  }
  return domainCreateOrTransferProducts;
});
export const selectSuggestUpsellParams = createSelector(selectCurrentId, localeSelector, selectCartCurrency, selectCartGrid, selectCountryCode, selectDomainRenewHasPremium, selectDomainRenewProductsFQDNs, (sharingId, locale, cartCurrency, cartGrid, countryCode, hasPremium, domainRenewProductsFQDNs) => ({
  sharing_id: sharingId,
  lang: locale,
  currency: cartCurrency,
  grid: cartGrid,
  country: countryCode,
  hasPremium,
  search: domainRenewProductsFQDNs
}));
export const selectCartTotal = createSelector([state => state.cart.total || EMPTY_OBJECT], total => ({
  taxes: total.taxes || EMPTY_ARRAY,
  taxAmount: total.tax_amount,
  afterTaxes: total.after_taxes,
  beforeTaxes: total.before_taxes,
  beforeDiscount: total.before_discount,
  discounts: total.discounts || undefined
}));
export const selectCartHasOptimisticProducts = createSelector(selectProducts, products => !!find(products, product => product.status === 'optimistic'));
const EMPTY_CATALOG_ARRAY = [];
export const selectCartProductCatalogEntries = createSelector(selectOneProduct, product => product?.catalog || EMPTY_CATALOG_ARRAY);
export const selectCartProductOptions = createSelector([selectCartProductCatalogEntries], productCatalog => {
  if (!productCatalog.length) {
    return EMPTY_OBJECT;
  }
  return productCatalog.reduce((acc, result) => {
    acc[result.options.phase] = union(range(result.min_duration, result.max_duration + 1), acc[result.options.phase]).sort((left, right) => parseInt(left, 10) - parseInt(right, 10));
    return acc;
  }, {});
});
export const selectPollerIdForProduct = createSelector(selectOneProduct, product => get(product, 'pollerId'));
export const selectProductCatalogIsPending = createSelector(selectOneProduct, product => get(product, 'pending', false));
export const selectCartOwner = state => state.cart.owner;
export const selectProductErrors = createSelector(selectRawProducts, products => {
  const allErrors = values(products || {}).filter(product => product.status === 'error').map(applyDecoratorsOnProduct).reduce((acc, product) => {
    const errors = (product.errors || []).reduce((acc2, error) => {
      acc2[error.name] = error.description;
      return acc2;
    }, {}) || [];
    return acc.concat({
      id: product.id,
      product,
      errors
    });
  }, []);
  return allErrors;
});
export const selectCartHasContracts = createSelector(selectRawContracts, contracts => values(contracts || {}).length > 0);
export const selectContracts = createSelector(selectRawContracts, contracts => values(contracts || {}).sort((a, b) => a.order > b.order ? 1 : -1));
export const hasValidatedAllContracts = createSelector(selectContracts, contracts => contracts.length > 0 && contracts.every(contract => contract.isApproved === true));
export const selectCartIsDeleted = state => state.cart.status === 'deleted';
export const selectCartIsReopenable = state => state.cart.isReopenable === true && state.cart.status === 'closed';
export const selectCartIsActive = state => state.cart.status === 'active';
export const selectCartIsCommitted = state => state.cart.status === 'closed';
export const selectCartIsCommitting = state => state.cart.isCommitting;
export const selectCartIsResuming = state => !!state.cart.isResuming;
export const selectCartIsNotCreated = state => state.cart.status === 'notCreated';
export const selectCartHasCommitError = state => state.cart.hasCommitError;
export const selectIsAnyCartProductWorking = createSelector(selectProducts, products => products.some(product => product.isWorking));
export const selectCartHasOrga = state => state.cart.hasOrga;
const EMPTY_PARTIAL_ORGA_ARRAY = [];
export const selectCartOwnerList = state => state.cart.ownerList.data.can_process || EMPTY_PARTIAL_ORGA_ARRAY;
export const selectHasCorporateProducts = state => state.cart.ownerList.data.corporate || false;
export const selectHasOwnerOnly = state => state.cart.ownerList.data.owner_only || false;
export const selectIsFetchingCartOwnerList = state => state.cart.ownerList.isFetching;
export const selectIsLoadedCartOwnerList = state => state.cart.ownerList.isLoaded;
export const selectIsFailureCartOwnerList = state => state.cart.ownerList.isFailure;
export const selectIsFetchingCartConfigurations = state => state.cart.configurations.isFetching;
export const selectIsLoadedCartConfigurations = state => state.cart.configurations.isLoaded;
export const selectIsFailureCartConfigurations = state => state.cart.configurations.isFailure;
export const selectCartConfigurations = state => state.cart.configurations.data;
export const selectNonBundledProducts = createSelector([selectProducts], products => products.filter(product => !product.bundleId));
export const selectBundledProducts = createSelector([selectProducts], products => products.filter(product => !!product.bundleId));
export const selectCartBundles = createSelector([selectBundledProducts], products => {
  const bundlesById = products.reduce((acc, product) => {
    if (typeof product.bundleId === 'undefined') {
      return acc;
    }
    const id = product.bundleId;
    const productBeforeDiscount = product.total?.before_discount || 0;
    const productAfterTaxes = product.total?.after_taxes || 0;
    const productBeforeTaxes = product.total?.before_taxes || 0;
    const productsbeforeDiscountAfterTaxes = product.total?.before_discount_after_taxes || 0;
    const productsbeforeDiscountBeforeTaxes = product.total?.before_discount_before_taxes || 0;
    acc[id] = {
      id: product.bundleId,
      priceBeforeDiscount: get(acc, `[${id}].priceBeforeDiscount`, 0) + productBeforeDiscount,
      priceAfterTaxes: get(acc, `[${id}].priceAfterTaxes`, 0) + productAfterTaxes,
      priceBeforeTaxes: get(acc, `[${id}].priceBeforeTaxes`, 0) + productBeforeTaxes,
      products: get(acc, `[${id}].products`, []).concat(product),
      productsbeforeDiscountAfterTaxes: get(acc, `[${id}].productsbeforeDiscountAfterTaxes`, 0) + productsbeforeDiscountAfterTaxes,
      productsbeforeDiscountBeforeTaxes: get(acc, `[${id}].productsbeforeDiscountBeforeTaxes`, 0) + productsbeforeDiscountBeforeTaxes
    };
    return acc;
  }, {});
  return Object.keys(bundlesById).map(bundleId => bundlesById[bundleId]);
});
export const selectCartBundleGrids = (state, bundleId) => {
  if (!state.cart.bundles) {
    return undefined;
  }
  const bundle = state.cart.bundles.find(({
    id
  }) => id === (typeof bundleId === 'number' ? bundleId : Number.parseInt(bundleId, 10)));
  if (!bundle) {
    return undefined;
  }
  return bundle.grids;
};
export const selectAllCartBundleGrids = createSelector([state => state.cart.bundles], bundles => {
  if (!bundles) return undefined;
  const grids = bundles.reduce((acc, bundle) => [...acc, ...bundle.grids], []);
  return [...new Set(grids)];
});
export const selectAllCartBundleTypes = state => {
  if (!state.cart.bundles) {
    return undefined;
  }
  const types = state.cart.bundles.reduce((acc, bundle) => [...acc, bundle.type], []);
  return [...new Set(types)];
};
export const selectIsCartIncludedBundleUseOnce = createSelector([state => state.cart.bundles], bundles => {
  if (!bundles) return undefined;
  return bundles.some(bundle => bundle.use_once);
});